import React, { useState, useCallback } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Alert } from '@/components/ui/Alert/Alert';
import { Badge } from '@/components/ui/Badge/Badge';
import { 
  AlertCircle, 
  MapPin, 
  Settings2, 
  CircleDot, 
  Building2,
  Loader2,
  Hotel,
  Database,
  Sparkles,
  Search
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  googlePlacesSearchService, 
  type HotelSearchResult, 
  type ExistingVenueInfo 
} from '@/services/GooglePlacesSearchService';
import { VenuesService } from '@/services/VenuesService';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import type { DestinationSearchMode, ResearchDestination } from '@/services/ResearchService';

/**
 * Preview status for destination settings
 */
interface PreviewStatus {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  hotels: HotelSearchResult[];
  existing_count: number;
  new_count: number;
  existing_venues: Record<string, ExistingVenueInfo>;
  error?: string;
}

interface EditDestinationModalProps {
  isOpen: boolean;
  onClose: () => void;
  destination: ResearchDestination;
  onUpdate: (destinationId: string, updates: {
    search_mode: DestinationSearchMode;
    search_radius_km: number | null;
    location_json?: any;
    display_name?: string | null;
  }, hotelsWereImported?: boolean) => Promise<void>;
}

export function EditDestinationModal({ 
  isOpen, 
  onClose, 
  destination,
  onUpdate,
}: EditDestinationModalProps) {
  const [searchMode, setSearchMode] = useState<DestinationSearchMode>(destination.search_mode);
  const [radiusKm, setRadiusKm] = useState(destination.search_radius_km || 15);
  const [displayNameOverride, setDisplayNameOverride] = useState(destination.display_name || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewStatus>({
    status: 'idle',
    hotels: [],
    existing_count: 0,
    new_count: 0,
    existing_venues: {},
  });
  const [triggerAiResearch, setTriggerAiResearch] = useState(true);
  const [locationSearchValue, setLocationSearchValue] = useState('');
  const [updatedLocation, setUpdatedLocation] = useState<LocationData | null>(null);

  const venuesService = new VenuesService();
  const isCountry = destination.city === destination.country;

  // Extract location from destination
  // Use updated location if available, otherwise use existing
  const locationJson = updatedLocation ? {
    formatted_address: updatedLocation.formatted_address,
    place_id: updatedLocation.place_id,
    geometry: {
      location: {
        lat: updatedLocation.lat,
        lng: updatedLocation.lon,
      },
    },
  } : destination.location_json;
  
  let location: { lat?: number; lng?: number } | null = null;
  let lat = 0;
  let lon = 0;
  
  if (locationJson) {
    // Try geometry.location structure (Google Places format)
    if (locationJson.geometry?.location) {
      location = locationJson.geometry.location;
      lat = location.lat || 0;
      lon = location.lng || 0;
    }
  }
  
  const formattedAddress = locationJson?.formatted_address || 
    (destination.city === destination.country 
      ? destination.country 
      : `${destination.city}, ${destination.country}`);
  
  const hasValidLocation = lat !== 0 && lon !== 0;
  
  function handleLocationSelect(location: LocationData) {
    setUpdatedLocation(location);
    setLocationSearchValue('');
    // Clear any preview errors
    if (preview.status === 'error') {
      setPreview({
        status: 'idle',
        hotels: [],
        existing_count: 0,
        new_count: 0,
        existing_venues: {},
      });
    }
  }

  function handleClose() {
    setSearchMode(destination.search_mode);
    setRadiusKm(destination.search_radius_km || 15);
    setDisplayNameOverride(destination.display_name || '');
    setError(null);
    setLocationSearchValue('');
    setUpdatedLocation(null);
    setPreview({
      status: 'idle',
      hotels: [],
      existing_count: 0,
      new_count: 0,
      existing_venues: {},
    });
    onClose();
  }

  const previewSearch = useCallback(async () => {
    if (isCountry) {
      setPreview({
        status: 'error',
        hotels: [],
        existing_count: 0,
        new_count: 0,
        existing_venues: {},
        error: 'Preview is not available for country destinations',
      });
      return;
    }

    if (!location || lat === 0 || lon === 0) {
      setPreview({
        status: 'error',
        hotels: [],
        existing_count: 0,
        new_count: 0,
        existing_venues: {},
        error: 'Location coordinates are missing. Please ensure the destination has valid location data.',
      });
      return;
    }

    setPreview({
      status: 'loading',
      hotels: [],
      existing_count: 0,
      new_count: 0,
      existing_venues: {},
    });

    const searchModeForApi = searchMode === 'radius' ? 'radius' : 'text';

    try {
      const result = await googlePlacesSearchService.previewImport({
        location: {
          lat,
          lon,
          name: formattedAddress,
        },
        search_mode: searchModeForApi,
        radius_km: searchMode === 'radius' ? radiusKm : 15,
      });

      setPreview({
        status: 'loaded',
        hotels: result.hotels,
        existing_count: result.existing_count,
        new_count: result.new_count,
        existing_venues: result.existing_venues,
      });
    } catch (err: any) {
      console.error('Preview search error:', err);
      setPreview({
        status: 'error',
        hotels: [],
        existing_count: 0,
        new_count: 0,
        existing_venues: {},
        error: err.message || 'Failed to search Google Places',
      });
    }
  }, [location, lat, lon, formattedAddress, searchMode, radiusKm, isCountry]);

  async function handleSave() {
    try {
      setIsUpdating(true);
      setError(null);

      // If preview shows new venues and user wants to import them
      if (triggerAiResearch && preview.status === 'loaded' && preview.new_count > 0) {
        const hotelsToImport = preview.hotels.filter(
          h => !preview.existing_venues[h.placeId]
        );

        if (hotelsToImport.length > 0) {
          const importResult = await googlePlacesSearchService.importHotels(
            hotelsToImport.map(h => ({
              placeId: h.placeId,
              name: h.name,
              address: h.address,
              lat: h.lat,
              lon: h.lon,
              rating: h.rating,
              userRatingCount: h.userRatingCount,
              phone: h.phone,
              website: h.website,
            }))
          );

          // Trigger AI research if enabled
          if (triggerAiResearch && importResult.importedIds.length > 0) {
            try {
              await venuesService.bulkAction('enrich', {
                allFiltered: false,
                ids: importResult.importedIds,
              });
            } catch (researchError) {
              console.error('AI research trigger failed:', researchError);
              // Don't fail the whole operation if research fails
            }
          }
        }
      }

      // Update destination settings and location if changed
      const updateData: any = {
        search_mode: searchMode,
        search_radius_km: searchMode === 'radius' ? radiusKm : null,
        display_name: displayNameOverride.trim() || null,
      };
      
      // If location was updated, include it
      if (updatedLocation) {
        updateData.location_json = {
          formatted_address: updatedLocation.formatted_address,
          place_id: updatedLocation.place_id,
          geometry: {
            location: {
              lat: updatedLocation.lat,
              lng: updatedLocation.lon,
            },
          },
        };
      }
      
      // Track if hotels were imported so we can refresh search results
      const hotelsWereImported = triggerAiResearch && preview.status === 'loaded' && preview.new_count > 0;
      
      await onUpdate(destination.id, updateData, hotelsWereImported);

      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update destination');
    } finally {
      setIsUpdating(false);
    }
  }

  const hasChanges = searchMode !== destination.search_mode ||
    (searchMode === 'radius' && radiusKm !== (destination.search_radius_km || 15)) ||
    updatedLocation !== null ||
    displayNameOverride.trim() !== (destination.display_name || '') ||
    (preview.status === 'loaded' && preview.new_count > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title="Edit Destination Settings"
      description={`Configure search settings for ${formattedAddress}`}
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            {preview.status === 'loaded' && preview.new_count > 0 && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={triggerAiResearch}
                  onChange={(e) => setTriggerAiResearch(e.target.checked)}
                  className="w-4 h-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                  disabled={isUpdating}
                />
                <Sparkles className="w-4 h-4 text-primary-600" />
                <span className="text-sm text-gray-700">Import & Research New Venues</span>
              </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={isUpdating}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!hasChanges || isUpdating}
              isLoading={isUpdating}
            >
              Save Changes
              {preview.status === 'loaded' && preview.new_count > 0 && triggerAiResearch && (
                <span className="ml-2">({preview.new_count} new)</span>
              )}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Destination Info */}
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {formattedAddress}
            </div>
            {hasValidLocation ? (
              <div className="text-sm text-gray-500">
                {lat.toFixed(4)}, {lon.toFixed(4)}
              </div>
            ) : (
              <div className="text-sm text-amber-600">
                Location coordinates missing
              </div>
            )}
          </div>
          {isCountry && (
            <Badge variant="secondary" className="flex-shrink-0">Country</Badge>
          )}
        </div>

        {/* Destination Name Override (cities only, not regions/countries) */}
        {!isCountry && destination.destination_type !== 'region' && (
          <div className="space-y-1.5">
            <label htmlFor="display-name-override" className="text-sm font-medium text-gray-700">
              Destination Name Override
            </label>
            <input
              id="display-name-override"
              type="text"
              value={displayNameOverride}
              onChange={(e) => setDisplayNameOverride(e.target.value)}
              placeholder={`e.g., "San Miguel" (leave empty to use default)`}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              disabled={isUpdating}
            />
            <p className="text-xs text-gray-500">
              Override the Google-provided name. Applies across all tabs and the client proposal.
            </p>
          </div>
        )}

        {/* Location Search (if coordinates missing) */}
        {!hasValidLocation && (
          <div className="space-y-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-900">
                Location coordinates required for preview
              </span>
            </div>
            <p className="text-sm text-amber-700 mb-3">
              Search for the location to populate coordinates and enable preview functionality.
            </p>
            <LocationAutocomplete
              value={locationSearchValue}
              onChange={setLocationSearchValue}
              onLocationSelect={handleLocationSelect}
              placeholder={`Search for ${formattedAddress}...`}
              types="cities"
              disabled={isUpdating}
            />
            {updatedLocation && (
              <div className="mt-2 p-2 bg-white rounded border border-amber-300">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-700">
                    Location updated: {updatedLocation.formatted_address}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {updatedLocation.lat.toFixed(4)}, {updatedLocation.lon.toFixed(4)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search Mode Settings (only for cities, not countries) */}
        {!isCountry ? (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Search Mode</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSearchMode('city_name')}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left',
                    searchMode === 'city_name'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <Building2 className={cn(
                    'w-5 h-5 flex-shrink-0',
                    searchMode === 'city_name' ? 'text-primary-600' : 'text-gray-400'
                  )} />
                  <div>
                    <div className={cn(
                      'font-medium text-sm',
                      searchMode === 'city_name' ? 'text-primary-900' : 'text-gray-700'
                    )}>
                      City Name
                    </div>
                    <div className="text-xs text-gray-500">
                      Match venues by city field
                    </div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setSearchMode('radius')}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left',
                    searchMode === 'radius'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <CircleDot className={cn(
                    'w-5 h-5 flex-shrink-0',
                    searchMode === 'radius' ? 'text-primary-600' : 'text-gray-400'
                  )} />
                  <div>
                    <div className={cn(
                      'font-medium text-sm',
                      searchMode === 'radius' ? 'text-primary-900' : 'text-gray-700'
                    )}>
                      Radius Search
                    </div>
                    <div className="text-xs text-gray-500">
                      Include nearby areas
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Radius Slider (only when radius mode is selected) */}
            {searchMode === 'radius' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Search Radius</span>
                  <span className="font-medium text-gray-900">{radiusKm} km</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={50}
                  step={1}
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>1 km</span>
                  <span>50 km</span>
                </div>
              </div>
            )}

            {/* Preview Button */}
            <Button
              variant="outline"
              onClick={previewSearch}
              disabled={preview.status === 'loading' || !hasValidLocation}
              className="w-full"
              leftIcon={preview.status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings2 className="w-4 h-4" />}
            >
              {preview.status === 'loading' ? 'Searching...' : 'Preview Search Results'}
            </Button>
            
            {!hasValidLocation && (
              <p className="text-xs text-amber-600 text-center">
                Please search for the location above to enable preview
              </p>
            )}

            {/* Preview Results */}
            {preview.status === 'loaded' && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Hotel className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">Search Preview</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-700">
                    Found {preview.hotels.length} hotels
                  </span>
                  {preview.existing_count > 0 && (
                    <span className="text-gray-600 flex items-center gap-1">
                      <Database className="w-4 h-4" />
                      {preview.existing_count} in database
                    </span>
                  )}
                  {preview.new_count > 0 && (
                    <span className="text-green-600 font-medium flex items-center gap-1">
                      <Hotel className="w-4 h-4" />
                      {preview.new_count} new
                    </span>
                  )}
                </div>
              </div>
            )}

            {preview.status === 'error' && (
              <Alert variant="error">
                <AlertCircle className="w-4 h-4" />
                <span>{preview.error}</span>
              </Alert>
            )}
          </>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Building2 className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-blue-800">
              Country destinations use name-based matching (radius search not available)
            </span>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </Alert>
        )}
      </div>
    </Modal>
  );
}

